import { useEffect, useState } from 'react'
import { api, ApiError, session } from '../lib/api'
import { useApi } from '../lib/hooks'
import { clock, num, shortDate } from '../lib/format'
import { Banner, Empty, IconCheck, Loading, Panel, Pill, StatMini } from '../ui/kit'
import { FieldHeader } from './FieldShell'

/**
 * Putting an order together in the store.
 *
 * The rule this screen exists to enforce: what goes in the crate is weighed, not
 * assumed. A line records what was actually packed, which is not always what was
 * ordered — the store runs short, a grade fails on the day. Recording it here means
 * the shortfall is known before the van leaves, instead of being discovered at the
 * buyer's door where it costs a relationship.
 */

interface PickLot { lotCode: string; allocatedKg: number }

interface PickLine {
  id: string
  cropName: string
  gradeCode: string
  orderedKg: number
  allocatedKg: number
  pickedKg: number | null
  lots: PickLot[]
}

interface PickOrder {
  id: string
  code: string
  status: string
  deliveryDate: string
  orderedKg: number
  total: number
  notes: string | null
  pickNotes: string | null
  startedAt: string | null
  readyAt: string | null
  pickerUserId: string | null
  pickerName: string | null
  buyerName: string
  address: string | null
  windowStart: string | null
  windowEnd: string | null
  depotName: string | null
  lines: PickLine[]
}

export function Packing() {
  const [mine, setMine] = useState(true)
  const queue = useApi<PickOrder[]>(`/picking/queue${mine ? '?mine=true' : ''}`, { deps: [mine] })
  const [open, setOpen] = useState<PickOrder | null>(null)
  const [banner, setBanner] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null)

  const me = session.user?.id
  const rows = queue.data ?? []
  const ready = rows.filter((o) => o.status === 'ready')
  const todo = rows.filter((o) => o.status !== 'ready')

  async function claim(o: PickOrder) {
    try {
      await api.post(`/picking/${o.id}/claim`)
      queue.reload()
      setOpen(o)
    } catch (e) {
      setBanner({ tone: 'crit', text: e instanceof ApiError ? e.message : 'Could not take that one.' })
    }
  }

  if (open) {
    return (
      <PackOne
        order={open}
        onClose={() => { setOpen(null); queue.reload() }}
        onDone={(msg) => { setBanner({ tone: 'ok', text: msg }); setOpen(null); queue.reload() }}
      />
    )
  }

  return (
    <>
      <FieldHeader />
      <div className="field-greet">
        <h1>To pack</h1>
        <p>Orders with stock set aside, waiting to be put together.</p>
      </div>

      <div className="field-body">
        {banner && (
          <Banner tone={banner.tone === 'ok' ? 'info' : 'crit'} title={banner.text}>{null}</Banner>
        )}

        <div className="field-chips">
          <button className={`btn ${mine ? '' : 'quiet'}`} style={chip} onClick={() => setMine(true)}>
            Mine
          </button>
          <button className={`btn ${mine ? 'quiet' : ''}`} style={chip} onClick={() => setMine(false)}>
            Everything
          </button>
        </div>

        {queue.loading && <Panel><Loading rows={4} /></Panel>}

        {!queue.loading && rows.length === 0 && (
          <Empty title={mine ? 'Nothing assigned to you' : 'Nothing to pack'}>
            {mine
              ? 'Tap Everything to see what else is waiting in the store.'
              : 'Every order with stock set aside has been packed.'}
          </Empty>
        )}

        {rows.length > 0 && (
          <div className="mini-row">
            <StatMini label="To pack" value={todo.length} />
            <StatMini label="Ready for pickup" value={ready.length}
              note={ready.length > 0 ? 'waiting by the door' : 'none yet'} />
          </div>
        )}

        {todo.length > 0 && (
          <Panel title="Waiting" meta={`${todo.length}`}>
            <div className="panel-body col" style={{ gap: 'var(--sp-6)' }}>
              {todo.map((o) => {
                const packedLines = o.lines.filter((l) => l.pickedKg !== null).length
                const isMine = o.pickerUserId === me
                return (
                  <div key={o.id} style={card}>
                    <div className="spread" style={{ alignItems: 'flex-start' }}>
                      <div style={{ minWidth: 0 }}>
                        <b style={{ fontSize: 'var(--fs-md)' }}>{o.buyerName}</b>
                        <div className="faint" style={{ fontSize: 'var(--fs-xs)' }}>
                          <span className="mono">{o.code}</span> · {num(o.orderedKg, 0)} kg
                          {' · '}{shortDate(o.deliveryDate)}
                          {o.windowStart && ` · by ${clock(o.windowStart)}`}
                        </div>
                      </div>
                      {o.pickerName
                        ? <Pill tone={isMine ? 'ok' : 'neutral'} dot={false}>
                            {isMine ? 'Yours' : o.pickerName.split(' ')[0]}
                          </Pill>
                        : <Pill tone="warn" dot={false}>Nobody yet</Pill>}
                    </div>

                    {packedLines > 0 && (
                      <p className="hint" style={{ marginTop: 'var(--sp-4)' }}>
                        {packedLines} of {o.lines.length} line{o.lines.length === 1 ? '' : 's'} packed
                      </p>
                    )}

                    <button
                      className="btn block"
                      style={{ marginTop: 'var(--sp-5)', minHeight: 40 }}
                      onClick={() => (o.pickerUserId ? setOpen(o) : void claim(o))}
                    >
                      {o.pickerUserId ? (isMine ? 'Carry on packing' : 'Open') : 'Take this one'}
                    </button>
                  </div>
                )
              })}
            </div>
          </Panel>
        )}

        {ready.length > 0 && (
          <Panel title="Ready for pickup" meta={`${ready.length}`}>
            <div className="panel-body" style={{ paddingTop: 4, paddingBottom: 4 }}>
              {ready.map((o) => {
                const packed = o.lines.reduce((s, l) => s + Number(l.pickedKg ?? 0), 0)
                const short = packed < Number(o.orderedKg) - 0.001
                return (
                  <button key={o.id} className="stop-row" style={{ width: '100%' }}
                    onClick={() => setOpen(o)}>
                    <span className="stop-state done">✓</span>
                    <span className="stop-who">
                      <b>{o.buyerName}</b>
                      <i>
                        <span className="mono">{o.code}</span>
                        {o.readyAt && ` · ready ${clock(o.readyAt.slice(11, 16))}`}
                      </i>
                    </span>
                    <span className="stop-amt">
                      <b>{num(packed, 0)} kg</b>
                      <span className={short ? 'warn-t' : ''}>
                        {short ? `${num(Number(o.orderedKg) - packed, 0)} kg short` : 'in full'}
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
          </Panel>
        )}
      </div>
    </>
  )
}

// ---------------------------------------------------------------- packing one
function PackOne({ order, onClose, onDone }: {
  order: PickOrder
  onClose: () => void
  onDone: (message: string) => void
}) {
  const [picked, setPicked] = useState<Record<string, string>>(() =>
    Object.fromEntries(order.lines.map((l) => [l.id, l.pickedKg === null ? '' : String(l.pickedKg)])))
  const [notes, setNotes] = useState(order.pickNotes ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // A line is only counted as packed once somebody has put a number against it.
  const anyPacked = order.lines.some((l) => (picked[l.id] ?? '').trim() !== '')
  const packedKg = order.lines.reduce((s, l) => s + (Number(picked[l.id]) || 0), 0)
  const shortBy = Number(order.orderedKg) - packedKg
  const isReady = order.status === 'ready'

  useEffect(() => { setSaved(false) }, [picked, notes])

  async function save() {
    setBusy(true); setError(null)
    try {
      await api.post(`/picking/${order.id}/pick`, {
        lines: order.lines
          .filter((l) => (picked[l.id] ?? '').trim() !== '')
          .map((l) => ({ orderLineId: l.id, pickedKg: Number(picked[l.id]) })),
        notes: notes.trim() || null,
      })
      setSaved(true)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.')
    } finally { setBusy(false) }
  }

  async function flagReady() {
    if (shortBy > 0.001 && !window.confirm(
      `You have packed ${num(packedKg, 1)} kg of the ${num(order.orderedKg, 1)} kg ordered.\n\n` +
      'Flag it for pickup anyway? The buyer will be short, and they will be told.',
    )) return

    setBusy(true); setError(null)
    try {
      await api.post(`/picking/${order.id}/pick`, {
        lines: order.lines
          .filter((l) => (picked[l.id] ?? '').trim() !== '')
          .map((l) => ({ orderLineId: l.id, pickedKg: Number(picked[l.id]) })),
        notes: notes.trim() || null,
      })
      await api.post(`/picking/${order.id}/ready`)
      onDone(`${order.code} is ready for pickup.`)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not flag it.')
    } finally { setBusy(false) }
  }

  async function reopen() {
    setBusy(true); setError(null)
    try {
      await api.post(`/picking/${order.id}/unready`)
      onDone(`${order.code} is back on the packing list.`)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not reopen it.')
    } finally { setBusy(false) }
  }

  return (
    <>
      <FieldHeader back={onClose} title="To pack" />
      <div className="field-greet">
        <h1>{order.buyerName}</h1>
        <p>
          <span className="mono">{order.code}</span> · {shortDate(order.deliveryDate)}
          {order.windowStart && ` · wants it by ${clock(order.windowStart)}`}
        </p>
      </div>

      <div className="field-body">
        {error && <Banner tone="crit" title={error}>{null}</Banner>}

        {isReady && (
          <Banner tone="info" title="This one is flagged for pickup">
            It is waiting for a driver. Reopen it below if something has to change.
          </Banner>
        )}

        {order.notes && (
          <Banner tone="info" title="The buyer said">{order.notes}</Banner>
        )}

        <Panel title="What to put in" meta={order.depotName ?? undefined}>
          <div className="panel-body col" style={{ gap: 'var(--sp-6)' }}>
            {order.lines.map((l) => {
              const v = Number(picked[l.id]) || 0
              const lineShort = (picked[l.id] ?? '').trim() !== '' && v < Number(l.orderedKg) - 0.001
              return (
                <div key={l.id} style={card}>
                  <div className="spread" style={{ alignItems: 'flex-start' }}>
                    <div>
                      <b style={{ fontSize: 'var(--fs-md)' }}>
                        {l.cropName} <Pill dot={false}>{l.gradeCode}</Pill>
                      </b>
                      <div className="faint" style={{ fontSize: 'var(--fs-xs)', marginTop: 2 }}>
                        {num(l.orderedKg, 0)} kg ordered
                      </div>
                    </div>
                  </div>

                  {l.lots.length > 0 && (
                    <div style={{ marginTop: 'var(--sp-4)' }}>
                      <div className="micro" style={{ marginBottom: 2 }}>Take from</div>
                      {l.lots.map((lot) => (
                        <div className="spread" key={lot.lotCode}
                          style={{ fontSize: 'var(--fs-sm)', padding: '1px 0' }}>
                          <span className="mono faint">{lot.lotCode}</span>
                          <span className="num">{num(lot.allocatedKg, 1)} kg</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="field" style={{ marginTop: 'var(--sp-5)', marginBottom: 0 }}>
                    <label htmlFor={`p-${l.id}`}>What you actually packed</label>
                    <input
                      id={`p-${l.id}`} className="input n" type="number" step="0.1"
                      inputMode="decimal" placeholder="kg" disabled={isReady}
                      value={picked[l.id] ?? ''}
                      onChange={(e) => setPicked((s) => ({ ...s, [l.id]: e.target.value }))}
                    />
                  </div>

                  {lineShort && (
                    <p className="hint warn-t" style={{ marginTop: 'var(--sp-4)' }}>
                      {num(Number(l.orderedKg) - v, 1)} kg short of what they ordered.
                    </p>
                  )}
                </div>
              )
            })}

            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor="picknotes">Anything the driver should know</label>
              <input id="picknotes" className="input" disabled={isReady}
                placeholder="Which crates, where they are standing…"
                value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
        </Panel>

        <div style={{
          background: 'var(--surface-2)', border: '1px solid var(--line-2)',
          borderRadius: 'var(--r-md)', padding: 'var(--sp-5) var(--sp-6)',
        }}>
          <div className="spread" style={{ fontSize: 'var(--fs-sm)' }}>
            <span className="muted">Ordered</span>
            <b className="num">{num(order.orderedKg, 1)} kg</b>
          </div>
          <div className="spread" style={{ fontSize: 'var(--fs-sm)' }}>
            <span className="muted">Packed</span>
            <b className="num">{num(packedKg, 1)} kg</b>
          </div>
          {shortBy > 0.001 && (
            <div className="spread" style={{ fontSize: 'var(--fs-sm)' }}>
              <span className="muted">Short</span>
              <b className="num warn-t">{num(shortBy, 1)} kg</b>
            </div>
          )}
          <p className="hint" style={{ marginTop: 'var(--sp-4)' }}>
            They are billed for what they take at the door, so a short load here is
            better known now than argued about there.
          </p>
        </div>

        {!isReady && (
          <>
            <button className="btn quiet block" disabled={busy || !anyPacked}
              onClick={() => void save()}>
              {busy ? 'Saving…' : saved ? 'Saved' : 'Save and carry on'}
            </button>

            <button className="btn lg block" disabled={busy || !anyPacked}
              onClick={() => void flagReady()}>
              <IconCheck size={16} /> Flag it for pickup
            </button>
          </>
        )}

        {isReady && (
          <button className="btn quiet block" disabled={busy} onClick={() => void reopen()}>
            Put it back on the packing list
          </button>
        )}
      </div>
    </>
  )
}

const card = {
  border: '1px solid var(--line)', borderRadius: 'var(--r-md)',
  padding: 'var(--sp-5) var(--sp-6)', background: 'var(--surface)',
} as const

const chip = { minHeight: 32, fontSize: 'var(--fs-xs)', padding: '0 var(--sp-6)' } as const
